import {observer} from 'mobx-react-lite';
import React, {useContext, useEffect} from 'react';
import {StoreContext} from '../base/root-store';
import {WebappBase} from '../dashboard/WebappBase';
import {
    IContentFiltersProps,
    IPageOptionProps,
    IPageSelectProps,
    IPageTitleAndFiltersProps,
    IQuestionProps,
    ITemplateFeedbackFeedbackProps,
    ITemplateFeedbackQuestionsProps,
    ITemplateGeneralCardProps,
    ITemplateGeneralRowProps,
    TemplateFeedbackDashboard,
} from '../../../patterns';
import {
    AnswerWithCommentOnFeedbackQuestionCard,
    FeedbackDashboardData,
    FeedbackQuestionCard,
    FeedbackStore,
} from './feedback-store';
import {IFeedbackItemProps} from '../../../patterns/components/feedback/feedback';
import {IAnswerProps} from '../../../patterns/components/feedback-answers/feedback-answers';
import {formatDateRange, formatToDayMonthAndYear, periodIdFromRange, rangeFromPeriodId} from '../base/dateUtils';
import {ISelectDatepickerProps} from '../../../patterns/components/forms/select/select';
import {DateRange} from 'react-day-picker';
import {ScoreTooltip} from '../base/scoretooltip/ScoreTooltip';

export const FeedbackPage = observer((): JSX.Element => {
    const store = useContext(StoreContext).feedbackStore;

    useEffect(() => {
        store.onEnter();
    }, []);

    return (
        <WebappBase
            store={store}
            title={store.label('feedback.title')}
            renderChildren={(): React.ReactNode => (
                <React.Fragment>
                    <TemplateFeedbackDashboard
                        pageTitleAndFilters={titleAndFilters(store)}
                        companies={companiesCardProps(store)}
                        segments={segmentsCardProps(store)}
                        feedback={feedbackCardProps(store)}
                        questions={questionProps(store)}
                    />
                </React.Fragment>
            )}
        />
    );
});

const titleAndFilters = (store: FeedbackStore): IPageTitleAndFiltersProps => (
    {
        title: store.label(`feedback.title`),
        contentFilters: feedbackPageFilterProps(store),
    }
);

const feedbackPageFilterProps = (store: FeedbackStore): IContentFiltersProps => ({
    filterValues: [
        {
            oneFilterValue: segmentFilterProps(store),
        },
        {
            oneFilterValue: companiesFilterProps(store),
        },
        {
            oneFilterValue: timeframeFilterProps(store),
        },
    ],
});

const segmentFilterProps = (store: FeedbackStore): IPageSelectProps => {
    const filterOptions = store.payload!.feedbackFilter.segments;

    const options = filterOptions.map((filterOption) => ({
        label: store.label(`common.filter.segment.${filterOption.id}`),
        value: filterOption.id,
    } as IPageOptionProps));

    return ({
        label: store.label('common.filter.segment.title'),
        name: 'segment',
        id: 'feedback-segment',
        defaultValue: filterOptions.filter((filterOption) => filterOption.selected)[0].id,
        options,
        onChange: (filterOptionsId): void => store.onSegmentChange('' + filterOptionsId),
        type: undefined,
    });
};

const companiesFilterProps = (store: FeedbackStore): IPageSelectProps => {
    const filterOptions = store.payload!.feedbackFilter.companies;

    const options = filterOptions.map((filterOption) => ({
        label: filterOption.label ? filterOption.label : store.label('common.filter.company.ALL'),
        value: filterOption.id,
    } as IPageOptionProps));

    return ({
        label: store.label('common.filter.company.title'),
        name: 'company',
        id: 'feedback-company',
        defaultValue: filterOptions.filter((filterOption) => filterOption.selected)[0].id,
        search: {
            id: 'company-search',
            name: 'Search',
            label: store.label('common.filter.search.label'),
            focusLabel: store.label('common.filter.search.focuslabel'),
        },
        options,
        onChange: (filterOptionsId): void => store.onCompanyChange('' + filterOptionsId),
        type: undefined,
    });
};

const timeframeFilterProps = (store: FeedbackStore): IPageSelectProps => {
    const filterOptions = store.payload!.feedbackFilter.periods;

    const options = filterOptions.map((filterOption) => ({
        label: store.label(`common.filter.period.${filterOption.id.split('_')[1]}`),
        value: filterOption.id,
    }));

    return ({
        label: store.label('common.filter.period.title'),
        id: 'feedback-time',
        defaultValue: store.variables.isCustomDate ? undefined : filterOptions.find((filterOption) => filterOption.selected)?.id,
        options,
        datepicker: getDatepickerProps(store),
        onChange: ((filterOptionId: string) => {
            store.setCustomDateSelected(false);
            store.onPeriodChange(filterOptionId);
        }),
    } as IPageSelectProps);
};

const getDatepickerProps: (store: FeedbackStore) => ISelectDatepickerProps = (store: FeedbackStore) => ({
    datepickerProps: {
        format: 'dd.MM.yyyy',
        type: 'range',
        defaultValue: {from: undefined, to: undefined} as DateRange,
        field: [
            {
                label: store.label('user-menu.datepicker.from'),
                id: 'datepicker-from',
                name: 'datepicker-from',
            },
            {
                label: store.label('user-menu.datepicker.to'),
                id: 'datepicker-to',
                name: 'datepicker-to',
            },
        ],
    },
    onChange: (range): void => {
        const rangeModifierValue = (range as DateRange);

        if (rangeModifierValue?.to && rangeModifierValue?.from) {
            const value = periodIdFromRange(rangeModifierValue);

            store.setCustomDateSelected(true);
            store.onPeriodChange(value);
        }
    },
    customDateDisplayValue: store.variables.isCustomDate && store.variables.periodId ? formatDateRange(rangeFromPeriodId(store.variables.periodId)) : undefined,
});

const companiesCardProps = (store: FeedbackStore): ITemplateGeneralCardProps | undefined => {
    if (store.payload!.companiesCard === undefined) {
        return undefined;
    } else {
        const dataScores = store.payload!.companiesCard.map((company) => company.barpoint.value.scorePercentage);

        return ({
            title: store.label('feedback.company.title'),
            table: {
                columns: [
                    {
                        header: store.label('feedback.company.header.company-name'),
                        width: '100%',
                        isSortable: true,
                    },
                    {
                        header: store.variables.periodId ? formatDateRange(rangeFromPeriodId(store.variables.periodId)) : '',
                        isSortable: true,
                    },
                    {
                        header: store.label('feedback.common.header.change'),
                        isSortable: true,
                    },
                    {
                        header: store.label('feedback.common.header.activity'),
                        isSortable: true,
                    },
                ],
                rows: store.payload!.companiesCard
                    .filter((company) => company.barpoint.value.answered >= 0)
                    .map((row) => appFeedbackDataRow(store, row, 'company', dataScores)),
            },
            more: {showExpand: true},
        });
    }
};

const segmentsCardProps = (store: FeedbackStore): ITemplateGeneralCardProps | undefined => {
    if (store.payload!.segmentsCard === undefined) {
        return undefined;
    } else {
        const segmentScores = store.payload!.segmentsCard.map((segment) => segment.barpoint.value.scorePercentage);

        return ({
            title: store.label('feedback.segments.title'),
            table: {
                columns: [
                    {
                        header: store.label('feedback.segments.header.segment'),
                        width: '100%',
                        isSortable: true,
                    },
                    {
                        header: store.variables.periodId ? formatDateRange(rangeFromPeriodId(store.variables.periodId)) : '',
                        isSortable: true,
                    },
                    {
                        header: store.label('feedback.common.header.change'),
                        isSortable: true,
                    },
                    {
                        header: store.label('feedback.common.header.activity'),
                        isSortable: true,
                    },
                ],
                rows: store.payload!.segmentsCard
                    .filter((segment) => segment.barpoint.value.answered >= 0)
                    .map((row) => appFeedbackDataRow(store, row, 'segment', segmentScores)),
            },
            more: {showExpand: true},
        });
    }
};

const appFeedbackDataRow = (store: FeedbackStore, data: FeedbackDashboardData, translationKey: string, dataScores: number[]): ITemplateGeneralRowProps => {
    const score = data.barpoint.value.scorePercentage;
    const companyLabel = data.value === 'ALL' ? store.label('feedback.common.all-companies') : data.value;
    const segmenTitleLabel = store.label(`common.filter.${translationKey}.${data.value}`);
    const segmentBusinessUnitName = store.payload!.feedbackTooltip.companyName ? store.payload!.feedbackTooltip.companyName : store.label('feedback.common.all-companies');
    const segmentCompareToName= store.payload!.feedbackTooltip.companyName !== undefined ? store.label('feedback.common.all-companies'): undefined;
    const segmentCompareTo= store.payload!.feedbackTooltip.companyName !== undefined ? data.barpoint.compareTo : undefined;

    let scorePercentageColor: string;

    if (Math.max(...dataScores) === score) {
        scorePercentageColor = 'green';
    } else if (Math.min(...dataScores) === score) {
        scorePercentageColor = 'red';
    } else {
        scorePercentageColor = 'grey';
    }

    let scoreChangeColor: string;

    if (data.scoreChange > 0) {
        scoreChangeColor = 'green';
    } else if (data.scoreChange < 0) {
        scoreChangeColor = 'red';
    } else {
        scoreChangeColor = 'grey';
    }

    return ({
        id: data.id,
        title: translationKey === 'company' ? companyLabel : segmenTitleLabel,
        score: {
            progress: {
                percent: score,
                color: score >= 50 ? 'green' : 'red',
            },
            indicator: {
                number: score,
                color: scorePercentageColor,
            },
        },
        scoreTooltip: store.payload!.feedbackTooltip !== undefined ? <ScoreTooltip
            period={store.payload!.feedbackTooltip.period}
            name={translationKey === 'company' ? companyLabel : segmentBusinessUnitName}
            value={data.barpoint.value}
            compareToName={translationKey === 'company' ? store.label('feedback.common.all-companies') : segmentCompareToName}
            compareTo={translationKey === 'company' ? data.barpoint.compareTo : segmentCompareTo}
        /> : undefined,
        change: {
            number: data.scoreChange,
            color: scoreChangeColor,
        },
        activity: {
            percent: data.barpoint.value.participationPercentage,
            color: 'blue',
        },
        activityTooltip: store.payload!.feedbackTooltip !== undefined ? <ScoreTooltip
            period={store.payload!.feedbackTooltip.period}
            name={translationKey === 'company' ? companyLabel : segmentBusinessUnitName}
            value={data.barpoint.value}
            compareToName={translationKey === 'company' ? store.label('feedback.common.all-companies') : segmentCompareToName}
            compareTo={translationKey === 'company' ? data.barpoint.compareTo : segmentCompareTo}
        /> : undefined,
        isFocused: data.focused,
    } as ITemplateGeneralRowProps);
};

const feedbackCardProps = (store: FeedbackStore): ITemplateFeedbackFeedbackProps | undefined => {
    if (store.payload!.moticheckFeedbackCard === undefined) {
        return undefined;
    } else {
        const questions: IFeedbackItemProps[] = store.payload!.moticheckFeedbackCard.map((question) => {
            const answers = question.answers.map((answer: AnswerWithCommentOnFeedbackQuestionCard): IAnswerProps => ({
                time: {
                    value: formatToDayMonthAndYear(answer.datetime),
                },
                direction: answer.answer === '100' || answer.answer === '75' ? 'up' : 'down',
                text: answer.text,
            }));

            return {
                title: store.label(`common.filter.segment.${question.segment}`),
                question: question.questionText,
                answers: {answers},
                id: question.feedbackQuestionId,
                count: answers.length,
            } as IFeedbackItemProps;
        });

        return ({
            title: store.label('feedback.feedback.title'),
            data: {
                items: questions.length === 0 ? {
                    type: 'info',
                    text: store.label('feedback.not-enough-data'),
                } : questions,
            },
            more: {showExpand: true},
            includeLikesAndComments: true,
        });
    }
};

const questionProps = (store: FeedbackStore): ITemplateFeedbackQuestionsProps | undefined => {
    if (store.payload!.questionCards === undefined || store.payload!.questionCards.length === 0) {
        return undefined;
    } else {
        const questionCards = store.payload!.questionCards.map((question) => questionCardProps(store, question));

        return ({
            items: questionCards,
        });
    }
};

const questionCardProps = (store: FeedbackStore, question: FeedbackQuestionCard): IQuestionProps => {
    const score = question.barpoint.value.scorePercentage;
    const activity = question.barpoint.value.participationPercentage;

    const answers = question.feedbackAnswers.map((answer: AnswerWithCommentOnFeedbackQuestionCard): IAnswerProps => ({
        direction: answer.answer === '100' || answer.answer === '75' ? 'up' : 'down',
        text: answer.text,
        time: {
            value: formatToDayMonthAndYear(answer.datetime),
        },
    }));

    return ({
        category: store.label(`common.filter.segment.${question.segment}`),
        text: question.questionText,
        answers: {answers},
        bars: [
            {
                icon: 'users',
                color: 'blue',
                label: store.label('feedback.questions.activity', {
                    number_of_answers: question.barpoint.value.answered.toString(),
                    number_of_questions_asked: question.barpoint.value.prepared.toString(),
                }),
                percent: activity,
            },
            {
                icon: 'like',
                color: score > 50 ? 'green' : 'red',
                label: store.label('feedback.questions.score', {score_percentage: score.toString()}),
                percent: score,
            },
        ],
    });
};

